import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { successMsg } from 'src/components/utilities/utils';
import { useParams } from 'react-router-dom';
import { useEventActions } from 'src/store/event/actions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/reducers/state';
interface IProps {
  setSelectedOption: (value: number) => void;
  excelData: Array<string> | undefined;
  demoImportFileData: Array<string> | undefined;
  fileData: any;
  setFileData: any;
}

const DO_NOT_IMPORT = 'Do not import this field';

const normalise = (str: string) =>
  str.toLowerCase().trim().replace(/\s+/g, ' ').replace(/\r/g, '');

const SelectField: React.FC<IProps> = ({
  setSelectedOption,
  excelData,
  demoImportFileData,
  fileData,
  setFileData
}) => {
  const { eventId } = useParams<string>();
  const dispatch = useDispatch();
  const eventAction = useEventActions(dispatch);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [mapFieldData, setMapFieldData] = useState<any>();
  const [finalExcelData, setFinalExcelData] = useState<any>();
  const [finalJsonOptionData, setFinalJsonOptionData] = useState<any>();
  const [excelValidationError, setExcelValidationError] = useState<any>();
  const [newData, setNewData] = useState<any>(excelData);
  const [errorValidation, setErrorValidation] = useState<boolean>(false);
  const [numberFieldError, setNumberFieldError] = useState<boolean>(false);
  const [skippedRows, setSkippedRows] = useState<Set<number>>(new Set());

  const { register, watch, setValue } = useForm();

  const { isRegistrationImported } = useSelector((state: RootState) => {
    return {
      isRegistrationImported: state.event.isRegistrationImported
    };
  });

  useEffect(() => {
    sessionStorage.setItem('initialExcel', JSON.stringify(excelData));
  }, []);

  useEffect(() => {
    if (isRegistrationImported) {
      setIsUploading(false);
    }
  }, [isRegistrationImported]);

  useEffect(() => {
    if (!excelData || !excelData.length || !demoImportFileData) return;

    const rawHeader = excelData[0].split(',');
    const allColumns: string[] = rawHeader.map((col) => col.replace(/\r/g, '').trim());

    const columnsWithData = new Set<string>();
    excelData.slice(1).forEach((row) => {
      const cells = row.split(',');
      cells.forEach((cell, colIndex) => {
        if (cell.replace(/\r/g, '').trim() !== '') {
          const colName = allColumns[colIndex];
          if (colName) columnsWithData.add(colName);
        }
      });
    });

    const excelColumns: string[] = allColumns.filter(
      (col) => col !== '' && col !== 'Last Login Date'
    );

    const finalExcelOptions = demoImportFileData.map((targetField) => ({
      field: targetField,
      excelOptionData: [...excelColumns, DO_NOT_IMPORT]
    }));

    setMapFieldData(finalExcelOptions);

    const initialSkipped = new Set<number>();

    demoImportFileData.forEach((targetField, index) => {
      const matchedColumn = excelColumns.find(
        (col) => normalise(col) === normalise(targetField)
      );

      if (matchedColumn && columnsWithData.has(matchedColumn)) {
        setValue(`item_${index}`, matchedColumn);
      } else {
        setValue(`item_${index}`, DO_NOT_IMPORT);
        initialSkipped.add(index);
      }
    });

    setSkippedRows(initialSkipped);
    setFinalExcelData(excelData.map((item) => item.split(',')));
  }, [excelData, demoImportFileData]);

  useEffect(() => {
    if (demoImportFileData && demoImportFileData.length) {
      const JsonData = demoImportFileData.map((item, index) => ({
        key: item,
        value: watch(`item_${index}`)
      }));
      setFinalJsonOptionData(JsonData);
    }
  }, [demoImportFileData, mapFieldData, watch]);


  const convertToCsv = (dataArray: string[]) =>
    new Blob([dataArray.join('\n')], { type: 'text/csv' });

  const handleOptionChange = (value: string, optionId: number) => {
    setValue(`item_${optionId}`, value);
    setErrorValidation(false);
    setNumberFieldError(false);

    setSkippedRows((prev) => {
      const updated = new Set(prev);
      value === DO_NOT_IMPORT ? updated.add(optionId) : updated.delete(optionId);
      return updated;
    });

    const JsonData =
      demoImportFileData &&
      demoImportFileData.map((item, index) => ({
        key: item,
        value: index === optionId ? value : watch(`item_${index}`)
      }));

    JsonData &&
      JsonData.forEach((item) => {
        if (item.key === 'Email' && item.value !== 'Email' && item.value !== DO_NOT_IMPORT) {
          setErrorValidation(true);
        }
      });

    setFinalJsonOptionData(JsonData);
  };

  function mapFields(e1: any[], e2: string[]) {
    const mapping: Record<string, string> = {};
    e1.forEach((item) => { mapping[item.key] = item.value; });

    const headerRaw = e2.shift()!.split(',');
    const header = headerRaw.map((h) => h.replace(/\r/g, '').trim());

    const newHeader = e1
      .filter((item) => item.value !== DO_NOT_IMPORT)
      .map((item) => item.key);

    const result = [
      newHeader.join(','),
      ...e2.map((row) => {
        const fields = row.split(',');
        const newRow = newHeader.map((key) => {
          const mappedCol = mapping[key];
          if (mappedCol) {
            const idx = header.findIndex((h) => h === mappedCol);
            return idx !== -1 ? (fields[idx] || '').replace(/\r/g, '').trim() : '';
          }
          return '';
        });
        return newRow.join(',');
      })
    ];

    setNewData(result);
    return result;
  }

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidNumber = (number: string) => !isNaN(Number(number)) && number.trim() !== '';

  const validateCSVData = (data: string[]) => {
    const headers = data[0].split(',');
    const emailIndex = headers.indexOf('Email');
    const contactNumberIndex = headers.indexOf('Contact Number');
    const memberNumberIndex = headers.indexOf('Member Number');
    const invalidEntries: { row: number; field: string; value: string }[] = [];

    for (let i = 1; i < data.length; i++) {
      const row = data[i].split(',');
      const email = row[emailIndex];
      const contactNumber = contactNumberIndex !== -1 ? row[contactNumberIndex] : null;
      const memberNumber = memberNumberIndex !== -1 ? row[memberNumberIndex] : null;

      if (email && !isValidEmail(email))
        invalidEntries.push({ row: i, field: 'Email', value: email });
      if (contactNumber && !isValidNumber(contactNumber))
        invalidEntries.push({ row: i, field: 'Contact Number', value: contactNumber });
      if (memberNumber && !isValidNumber(memberNumber))
        invalidEntries.push({ row: i, field: 'Member Number', value: memberNumber });
    }
    return invalidEntries;
  };

  const handleSaveExcelData = () => {
    setErrorValidation(false);
    setNumberFieldError(false);

    const JsonData =
      demoImportFileData &&
      demoImportFileData.map((item, index) => ({
        key: item,
        value: watch(`item_${index}`)
      }));

    const emailMapping = JsonData && JsonData.find((item) => item.key === 'Email');
    if (!emailMapping || emailMapping.value === DO_NOT_IMPORT) {
      setErrorValidation(true);
      return;
    }

    const storedExcel = sessionStorage.getItem('initialExcel');
    const parsedExcel: string[] = storedExcel ? JSON.parse(storedExcel) : [];

    const finalMappingData = mapFields(JsonData!, parsedExcel);

    const newArray = finalMappingData.filter(
      (value) => value.trim() !== '' && value.trim() !== ','.repeat(value.trim().length)
    );

    const cleanData = newArray.map((item) => item.replace(/\r/g, ''));
    const validateEmail = validateCSVData(cleanData);

    if (validateEmail.length) {
      validateEmail.forEach((item) => {
        if (item.field === 'Member Number' || item.field === 'Contact Number')
          setNumberFieldError(true);
        if (item.field === 'Email')
          setErrorValidation(true);
      });
    } else {
      const csvData = convertToCsv(cleanData);
      setFileData(csvData);
      const formData = new FormData();
      formData.append('membercsv', csvData);
      setIsUploading(true);
      eventAction.importRegistrationRequest({ id: eventId, csvdata: formData });
    }
  };

  useEffect(() => {
    excelValidationError &&
      (excelValidationError.email ||
        excelValidationError.firstName ||
        excelValidationError.lastName ||
        excelValidationError.organization ||
        excelValidationError.jobTitle) &&
      successMsg('Email, FirstName, LastName, Organization And JobTitle Column can not be empty');
  }, [excelValidationError]);

  return (
    <div className="px-4 py-12">
      <div className="overflow-x-auto">
        {errorValidation && (
          <p className="flex justify-center text-red-500">
            Email must be mapped to a valid column and cannot be set to &quot;Do not import&quot;
          </p>
        )}
        {numberFieldError && (
          <p className="flex justify-center text-red-500">
            Number Fields should be mapped to Number field only
          </p>
        )}
        <table className="table-auto w-full border">
          <thead className="text-xs font-semibold uppercase bg-gray-100">
            <tr>
              <th className="p-2">
                <div className="font-semibold text-left">Column To Import</div>
              </th>
              <th className="p-2">
                <div className="font-semibold text-left">Map Into Field</div>
              </th>
            </tr>
          </thead>
          <tbody className="text-sm divide-y divide-gray-100">
            {mapFieldData &&
              mapFieldData.map((item: any, index: number) => {
                const isSkipped = skippedRows.has(index);
                return (
                  <tr key={index} className={isSkipped ? 'bg-gray-100 opacity-50' : ''}>
                    <td className={`p-2 ${isSkipped ? 'text-gray-400 line-through' : ''}`}>
                      {item.field}
                    </td>
                    <td className="p-2">
                      <select
                        {...register(`item_${index}`)}
                        onChange={(e) => handleOptionChange(e.target.value, index)}
                        className={`input-style ${isSkipped ? 'text-gray-400' : ''}`}>
                        {item.excelOptionData
                          .filter(
                            (opt: string) =>
                              opt !== null && opt !== undefined && opt.trim() !== ''
                          )
                          .map((opt: string, secondIndex: number) => (
                            <option key={secondIndex} value={opt}>
                              {opt}
                            </option>
                          ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        <div className="mt-3 flex justify-end">
          {!errorValidation && !numberFieldError && (
            <button
              onClick={handleSaveExcelData}
              type="button"
              disabled={isUploading}
              className="btn-primary w-auto mt-0 mr-3 ml-0 borderonly-btn flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
              {isUploading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-current"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12" cy="12" r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Uploading...
                </>
              ) : (
                'Continue'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectField;